import { UBButton } from "../../../Libraries";
import useGetSellerProducts from "../../../hooks/useGetSellerProducts";
import { useContext, useEffect, useRef, useState } from "react";
import StockList from "../../container/StockManagementFlow/StockList";
import { LanguageContext } from "../../../context/LanguageContext";
import useGetCategories from "../../../hooks/useGetCategories";
import BusinessLoader from "../../common/BusinessLoader";
import fourOFour from "../../../assets/images/404.webp";
import { setCurrentScreen } from "firebase/analytics";
import { analytics, triggerLogEvent } from "../../../config/firebase";
import {
  AppNonIdeal,
  InnerWrapper,
  MainContainer,
  TitleAndActionBox,
} from "../../common";
import { handleApi } from "../../../apis";
import { Grid, CircularProgress, Box } from "@mui/material";
import { toast } from "react-toastify";
import { getStockFileTemplate } from "../../../SyncService";
import { useNavigate } from "react-router-dom";
import NonIdealNew from "../../common/NonIdealNew";
import { useFiltersHandler } from "../../../hooks/useFilterHandler";

const StockManagement = ({}) => {
  let isSeller = localStorage.getItem("is_marketplace_seller");
  const [I18n] = useContext(LanguageContext);
  const {
    filters,
    appliedChips,
    handleFilterChange,
    resetFilters,
    handleRemoveChip,
    isResetFilter,
    setIsResetFilter,
    isAppliedFilter,
  } = useFiltersHandler(undefined, I18n);

  const { handleGetProducts, stockList, loading } =
    useGetSellerProducts(isSeller) || {};
  const { getCategories, catLoading, categories } = useGetCategories();

  const { productsLoad, pageLoad } = loading || {};
  const { stockCount, list } = stockList || {};
  const navigate = useNavigate();



  const [stockTypeModal, setStockTypeModal] = useState(true);
  const [bulkFileModal, setBulkFileModal] = useState(false);
  const [fileLoading, setFileLoading] = useState(false);
  const [fileToUpload, setFileToUpload] = useState({});
  const [searchLoad, setSearchLoad] = useState(false);
  const [details, setDetails] = useState(false);
  const [create, setCreate] = useState(false);

  let cancelToken = useRef();
  const searchDebounceRef = useRef(null);
  const prevFiltersRef = useRef(null);

  useEffect(() => {
    setCurrentScreen(analytics, "StockManagement_screen");
  }, []);

  const handleGetFilterList = () => {
    if (isAppliedFilter) {
      let params = {
        limit: 15,
        offset: 0,
      };

      if (
        Array.isArray(filters?.status)
          ? filters?.status?.length
          : filters?.status
      ) {
        params.in_stock =
          filters?.status?.toLowerCase() == "in stock" ? true : false;
      }

      if (filters?.visibility) {
        params.approval_status =
          filters?.visibility?.toLowerCase() == "published"
            ? "publish"
            : "unpublished";
      }

      if (filters?.keywords) {
        params.keyword = filters.keywords;
      }

      handleGetProducts(null, null, params);
    }
  };

  const handleGetMoreData = () => {
    let params = {
      limit: 15,
      offset: list.length,
    };

    if (isAppliedFilter) {
      if (
        Array.isArray(filters?.status)
          ? filters?.status?.length
          : filters?.status
      ) {
        params.in_stock =
          filters?.status?.toLowerCase() == "in stock" ? true : false;
      }

      if (filters?.visibility) {
        params.approval_status =
          filters?.visibility?.toLowerCase() == "published"
            ? "publish"
            : "unpublished";
      }

      if (filters?.keywords) {
        params.keyword = filters.keywords;
      }

      handleGetProducts(null, null, params);
    } else {
      handleGetProducts(params.offset, params.limit);
    }
  };

  useEffect(() => {
    const prevFilters = prevFiltersRef.current;
    // This screen is API-backed (GET_SELLER_PRODUCTS), so only the search box needs debouncing.
    const isKeywordsOnlyChange =
      prevFilters &&
      prevFilters.keywords !== filters.keywords &&
      Object.keys(filters).every(
        (key) => key === "keywords" || filters[key] === prevFilters[key],
      );
    prevFiltersRef.current = filters;

    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
      searchDebounceRef.current = null;
    }

    if (isKeywordsOnlyChange) {
      searchDebounceRef.current = setTimeout(handleGetFilterList, 500);
    } else {
      handleGetFilterList();
    }

    return () => {
      if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    };
  }, [filters]);

  /**
   * handling create stock type
   * @returns open create, edit stock screen and N/A
   */
  const handleStockType = (type) => {
    if (type == "create") {
      triggerLogEvent(analytics, "sc_stock_create", { type });
      toggleStockTypeModal();
      navigate("/SaddarInventory/CreateStock");
    } else {
      triggerLogEvent(analytics, "sc_stock_bulk_upload", { type });
      toggleStockTypeModal();
      toggleBulkFileModal(true);
    }
  };

  /**
   * handling bulk upload file
   * @returns toggle modal and set file
   */

  const handleDownloadTemplate = async () => {
    try {
      setFileLoading(true);
      let downloadTemplate = await getStockFileTemplate();

      const url = window.URL.createObjectURL(new Blob([downloadTemplate]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `${Date.now()}.xlsx`);
      document.body.appendChild(link);
      link.click();
      triggerLogEvent(analytics, "sb_bulk_temp_dwnld", {});

      toggleBulkFileModal();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Something went wrong", {
        className: "errorToast",
      });
    } finally {
      setFileLoading(false);
    }
  };

  /**
   * Toggle creaet stock type modal by params (toggle)
   * @returns true or false
   */
  const toggleStockTypeModal = (toggle = false) => setStockTypeModal(toggle);

  /**
   * Toggle bulk upload modal by params (toggle)
   * @returns true or false
   */
  const toggleBulkFileModal = (toggle = false) => setBulkFileModal(toggle);

  /**
   * Open stock details page by params (toggle) and clear search and search list if exist
   * @returns selected stoclk or false
   */
  const handleStockDetails = async (detail = false) => {
    navigate(`/SaddarInventory/StockDetail/${detail.id}`);
  };

  /**
   * Open create stock page by params (toggle) and get categories if not exist
   * @returns true or false
   */
  const handleCreateEditStock = async (toggle = false) => {
    if (!Object.keys(categories).length) {
      try {
        await getCategories();
      } catch {
        return; // hook already shows the error toast
      }
    }
    setCreate(toggle);
  };

  /**
   * Receive params for create, edit, publish and delect stock.
   * Referesh key to updtae the detail stock page
   * @returns updated stock or updated stock list
   */
  const handleStock = (endpoint, params, refresh) => {
    return new Promise(async (resolve, reject) => {
      try {
        let stock = await handleApi(endpoint, params, null, navigate);

        isAppliedFilter ? handleGetFilterList() : handleGetProducts();
        refresh && setDetails(stock.updated_data);
        resolve();
      } catch (err) {
        reject(err);
      }
    });
  };

  return isSeller ? (
    <MainContainer fluid className="quickSalePage">
      <Grid container>
        <Grid item md={12}>
          <TitleAndActionBox
            data={{
              title: "stock_management_small",
              isBack: true,
            }}
          >
            <UBButton
              title={I18n.create_item}
              id="inv_list_report_btn"
              className={
                "ubPrimaryButton fontSize12 fontWeight400 textUppercase marginLeft12"
              }
              onClick={() => handleStockType("create")}
            />
          </TitleAndActionBox>

          <InnerWrapper className="innerWrapper newInnerWrapper">
            <Box className={"AccountWrapper"}>
              {productsLoad ? (
                <div className="orderLoaderMain">
                  <CircularProgress size={24} className="loaderCircle" />
                </div>
              ) : list?.length || isAppliedFilter ? (
                <StockList
                  data={{
                    handlePagination: () => handleGetMoreData(),
                    stockCount: stockCount,
                    list: list,
                    toggleStockTypeModal,
                    toggleBulkFileModal,
                    handleStockDetails,
                    handleStock,
                    pageLoad,
                    I18n,
                    filters,
                    appliedChips,
                    handleFilterChange,
                    resetFilters,
                    handleRemoveChip,
                    isResetFilter,
                    setIsResetFilter,
                    handleGetProducts,
                    isAppliedFilter,
                  }}
                />
              ) : (
                <NonIdealNew
                  title={I18n.stock_management}
                  subtitle={I18n.create_item_in_easy_steps_and_manage_stock}
                  button1Title="create_item"
                  onButton1Click={() => handleStockType("create")}
                  loading={false}
                  disabledOne={false}
                  buttonOneId={"create_item"}
                />
              )}
            </Box>

            <BusinessLoader loadBusiness={catLoading} />
          </InnerWrapper>
        </Grid>
      </Grid>
    </MainContainer>
  ) : (
    <MainContainer fluid>
      <Grid container>
        <Grid item md={12}>
          <TitleAndActionBox
            data={{
              title: "stock_management_small",
              isBack: true,
            }}
          />
          <InnerWrapper className="innerWrapper newInnerWrapper">
            <AppNonIdeal
              data={{
                title: I18n.page_not_found,
                subtitle: I18n.we_re_sorry_the_page_you_requested,
                image: fourOFour,
                I18n,
              }}
            />
          </InnerWrapper>
        </Grid>
      </Grid>
    </MainContainer>
  );
};

export default StockManagement;
